// components/account/AccountHistoricsModal/components/DocumentItem.jsx
import { Card, Badge } from 'react-bootstrap';
import { 
  FaFilePdf, 
  FaFileImage, 
  FaFile,
  FaDownload,
  FaExternalLinkAlt,
  FaCalendarAlt,
  FaTag
} from 'react-icons/fa';
import { formatDate } from '../../service/utils/formatters';

/**
 * Item de document individuel - Affiche les détails d'un document
 */
const DocumentItem = ({ document, onOpen, onDownload }) => {
  /**
   * Détermine l'icône selon le type de fichier
   */
  const getFileIcon = () => {
    switch (document.type) {
      case 'PDF':
        return <FaFilePdf className="text-danger me-2" size={20} />;
      case 'IMAGE':
        return <FaFileImage className="text-success me-2" size={20} />;
      default:
        return <FaFile className="text-secondary me-2" size={20} />;
    }
  };

  /**
   * Détermine le badge du container
   */
  const getContainerBadge = () => {
    const containerLabel = document.container === 'documents' ? 'Cash' : 'Transaction';
    const badgeColor = document.container === 'documents' ? 'primary' : 'info';
    
    return (
      <Badge bg={badgeColor} className="ms-2">
        {containerLabel}
      </Badge>
    );
  };

  /**
   * Formate le nom du fichier pour l'affichage
   */
  const formatFileName = (fileName) => {
    if (!fileName) return 'Document';
    
    // Si le nom est trop long, on le tronque
    if (fileName.length > 30) {
      return fileName.substring(0, 27) + '...';
    }
    
    return fileName;
  };

  /**
   * Affiche le libellé de la catégorie de document
   */
  const getDocumentCategoryLabel = () => {
    const categories = {
      'PREUVE_DEPOT_CASH': 'Dépôt Cash',
      'PREUVE_TRANSACTION': 'Transaction',
      'DOCUMENT_COMPTABLE': 'Comptable',
      'PREUVE_ACHAT': 'Achat',
      'PREUVE_VENTE': 'Vente'
    };
    
    return categories[document.documentCategory] || document.documentCategory;
  };

  return (
    <Card className="border h-100">
      <Card.Body>
        {/* En-tête du document */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            {getFileIcon()}
            <div>
              <h6 className="mb-0 d-flex align-items-center">
                {formatFileName(document.fileName)}
                {getContainerBadge()}
              </h6>
              <small className="text-muted">
                {document.type} • {getDocumentCategoryLabel()}
              </small>
            </div>
          </div>
          
          {/* Actions */}
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={onOpen}
              title="Ouvrir dans un nouvel onglet"
            >
              <FaExternalLinkAlt size={12} />
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={onDownload}
              title="Télécharger"
            >
              <FaDownload size={12} />
            </button>
          </div>
        </div>

        {/* Détails du document */}
        <div className="mb-3">
          <h6 className="text-truncate" title={document.name}>
            {document.name}
          </h6>
          <div className="d-flex align-items-center text-muted mb-2">
            <FaTag className="me-2" size={12} />
            <small>{document.categorie} • {document.descriptif}</small>
          </div>
          <div className="d-flex align-items-center text-muted">
            <FaCalendarAlt className="me-2" size={12} />
            <small>{formatDate(document.date)}</small>
          </div>
        </div>

        {/* Informations financières */}
        <div className="border-top pt-3">
          <div className="row">
            <div className="col-6">
              <small className="text-muted d-block">Quantité</small>
              <strong className={document.quantity >= 0 ? 'text-success' : 'text-danger'}>
                {document.quantity >= 0 ? '+' : ''}{document.quantity}
              </strong>
            </div>
            <div className="col-6 text-end">
              <small className="text-muted d-block">Valeur</small>
              <strong>{document.lastValue.toLocaleString()}</strong>
            </div>
          </div>
        </div>

        {/* Information d'expiration */}
        {document.expiryTime && new Date(document.expiryTime) > new Date() && (
          <div className="mt-3">
            <div className="alert alert-info small p-2 mb-0">
              <small>
                <FaCalendarAlt className="me-1" />
                Lien valide jusqu'au {formatDate(document.expiryTime)}
              </small>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DocumentItem;